import { ISharepointDataDto } from "../interfaces/ISharepointDataDto";
import authModule from "../../../utilities/AuthModule";
import { IFileToUpload } from "../interfaces/IFileToUpload";
import { OneDriveOverWriteEnum } from "../components/enums/oneDriveOverWriteEnum";
import { IOneDriveDataDto } from "../interfaces/IOneDriveDataDto";
import { BasePersistor } from "./BasePersistor";
import { IPersistor } from "../interfaces/IPersistor";
import { API } from "../config/constants";
import { SavingLocationState } from "../types";
import { ModeEnum } from "../components/enums/modeEnum";
import { IMailSendPayload } from "../interfaces/IMailSendPayload";
import { OutlookUtils } from "../../../utilities/OutlookUtils"
import { FolderTypeEnum } from "../components/enums/FolderTypeEnum";
import { logger } from "../../../utilities/logger";
import { CheckinTypes, ListSettings } from "../components/_v2/redux/api/local/api-local-interfaces";
import { FileMetaData } from "../interfaces/props/FileMetaData";
import { apiFileSystemCallDynamicEndpoint } from "./ApiService";
import { SaveMode } from "../components/enums/SaveModeEnum";
import { IPageSettings } from "../interfaces/IPageSettings";
import { saveEmail } from "./TessService";
import { OutlookService } from "./OutlookService";
import { IOutlookRestDto } from "../interfaces/IOutlookRestDto";
import { selectedExchangeItemsStore } from "./selectedExchangeItemsStore";
import { discoverMailboxTarget } from "./sharedMailboxDiscovery";
import { getAuthenticatedClient } from "./GraphClientService";

export class OutlookPersistor extends BasePersistor implements IPersistor {
  mailSendPayload: IMailSendPayload;
  mode: ModeEnum;
  disableMailCategorization?: boolean;
  isTessEnabled?: boolean;
  workingDirectories?: IPageSettings[];

  constructor(mailSendPayload: IMailSendPayload, mode: ModeEnum, disableMailCategorization: boolean, isTessEnabled: boolean, workingDirectories: IPageSettings[]) {
    super();
    this.mailSendPayload = mailSendPayload;
    this.mode = mode;
    this.disableMailCategorization = disableMailCategorization;
    this.isTessEnabled = isTessEnabled;
    this.workingDirectories = workingDirectories;
  }

  // Multi-select leaves Office.context.mailbox.item null, so the item-based
  // shared-mailbox detection (getRestURL / getMailboxAddress) cannot run at
  // save time and would silently address the signed-in user's own mailbox —
  // the backend would then fetch every selected message from the wrong store.
  // Reuse the generation-guarded mailbox target the multi-select load probed:
  // it describes exactly the selection these files came from. When the load
  // left no target (raced re-render, skipped discovery), the save resolves on
  // its own: the files' rest ids plus the recorded contexts are all the
  // discovery needs, so resolution at the point of use never depends on an
  // earlier publish having survived. For a shared target the EWS routing
  // fields are also suppressed: EWS is not supported for shared-mailbox
  // scenarios, and the sticky ewsOnlySupport flag can survive from an earlier
  // single-item view and misroute the backend.
  private buildOutlookRestInfo = async (outlookToken: string, isDraft: boolean, firstGraphMailItemId?: string): Promise<IOutlookRestDto> => {
    const isMultiSelectContext: boolean = !Office.context?.mailbox?.item && this.mode !== ModeEnum.mailCompose;

    let mailboxTarget = isMultiSelectContext
      ? await selectedExchangeItemsStore.getMailboxTargetAsync()
      : null;

    if (isMultiSelectContext && !mailboxTarget && firstGraphMailItemId) {
      mailboxTarget = await discoverMailboxTarget(
        getAuthenticatedClient(await authModule.getGraphAccessToken()),
        firstGraphMailItemId);
    }

    const ewsOnlySupport: boolean = mailboxTarget ? false : OutlookUtils.ewsOnlySupport;
    return {
      ewsOnlySupport,
      url: mailboxTarget?.targetRestUrl
        ? `${mailboxTarget.targetRestUrl}/v2.0/users/${mailboxTarget.targetMailbox}`
        : await OutlookUtils.getRestURL(),
      outlookToken: outlookToken,
      ewsUrl: ewsOnlySupport ? OutlookUtils.getEWSURL() : undefined,
      ewsToken: ewsOnlySupport ? await OutlookUtils.getEWSToken() : undefined,
      mailboxToken: await authModule.getGraphAccessToken(),
      mailboxUser: mailboxTarget ? mailboxTarget.targetMailbox : await OutlookUtils.getMailboxAddress(),
      isDraft
    };
  };

  saveToSharepoint = async (
    selectedFiles: IFileToUpload[],
    savingLocation: SavingLocationState,
    autoFolderCreation: boolean,
    fileMetaData: FileMetaData[],
    accountId: string,
    informationProtectionId: string,
    outlookToken: string,
    outlookFolderType: FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel | FolderTypeEnum.docubird,
    autoRemoveMailItemAttachments?: boolean,
    convertEmailToPDF?: boolean,
    tenantOcrLanguage?: string,
    tenantPiiEnabled?: boolean,
    tenantReplaceFileNameWithGUID?: boolean,
    listSettings?: ListSettings,
    checkinType?: CheckinTypes,
    ensureUniqueFileName?: boolean,
    exchangeAccountEmail?: string,
    graphMailItemCategory?: string,
    saveMode?: SaveMode
  ): Promise<any> => {
    const userAuthData = await authModule.getAuthData();
    if (!userAuthData || !userAuthData.account) {
      authModule.signOut();
      return null;
    }

    const emailItemId: string = this.mode !== ModeEnum.mailCompose
      ? Office.context.mailbox?.item?.itemId
      : await OutlookUtils.getComposeModeItemId();

    if (emailItemId && this.mode === ModeEnum.mailCompose) {
      await OutlookUtils.verifyMailboxAccess(true, this.mode);
    }

    let dataToSend: ISharepointDataDto = {
      sharepointInfo: {
        graphToken: userAuthData.graphToken,
        sharepointToken: userAuthData.sharePointToken,
        siteUrl: savingLocation.webUrl,
        documentLibrary: savingLocation.documentLibrary,
        sharepointListId: savingLocation.sharePointListId,
        sharePointUniqueId: savingLocation.sharePointUniqueId,
        directory: savingLocation.directory,
        autoFolderCreation: autoFolderCreation,
        ensureUniqueFileName,
        checkinType: checkinType
      },
      outlookRestInfo: await this.buildOutlookRestInfo(
        outlookToken,
        !!(emailItemId && this.mode === ModeEnum.mailCompose),
        selectedFiles?.find(file => file.graphMailItemId)?.graphMailItemId),
      emailItemId: OutlookService.getItemRestId(emailItemId) ?? undefined,
      fileContents: selectedFiles,
      fileMetadataList: fileMetaData.map(file => {
        return {
          fileId: file.fileId,
          metaData: this.mapFileMetadata(file)
        }
      }),
      tenantGuid: userAuthData.account.tenantId,
      accountId: accountId,
      username: userAuthData.account.username,
      sensitivityLabelId: informationProtectionId,
      listSettings,
      exchangeAccountEmail,
      graphMailItemCategory
    };

    if (!this.disableMailCategorization) {
      const categoryToAssign: Office.CategoryDetails = OutlookUtils.getCategoryDetails(outlookFolderType);
      if (categoryToAssign && !(await OutlookUtils.getItemCategories())?.some(x => x.displayName === categoryToAssign.displayName)) {
        OutlookUtils.ensureMasterCategoryExists(categoryToAssign);
        dataToSend.categoriesToAssign = [categoryToAssign.displayName];
      }
    }

    dataToSend.autoRemoveMailItemAttachments = autoRemoveMailItemAttachments === true;
    dataToSend.convertEmailToPDF = convertEmailToPDF === true;
    dataToSend.tenantOcrLanguage = tenantOcrLanguage;
    dataToSend.tenantPiiEnabled = tenantPiiEnabled;
    dataToSend.tenantReplaceFileNameWithGUID = tenantReplaceFileNameWithGUID;
    dataToSend.exchangeAccountEmail = exchangeAccountEmail;
    dataToSend.graphMailItemCategory = graphMailItemCategory;
    dataToSend.saveMode = saveMode;
    dataToSend.teamId = savingLocation.teamId;
    dataToSend.channelId = savingLocation.channelId;

    try {
      const result = await apiFileSystemCallDynamicEndpoint(API.url.outlook.sharepoint, false, dataToSend);

      if (this.isTessEnabled && selectedFiles?.some(f => f.isMainMailItem)) {
        const tessAccountId: string = this.workingDirectories.find(dir => dir.tessAccountId)?.tessAccountId;
        if (tessAccountId && Array.isArray(result.succeededFiles) && result.succeededFiles.length > 0) {
          const tessProjectId: string = this.workingDirectories.find(dir => dir.tessProjectId)?.tessProjectId;
          const mailItem: Office.MessageCompose | any = this.mode !== ModeEnum.mailCompose
            ? Office.context.mailbox?.item
            : await OutlookUtils.getDraftMassage();
          saveEmail(tessAccountId, tessProjectId, savingLocation.webUrl, this.mode, mailItem, result.succeededFiles);
        }
      }

      if (!this.disableMailCategorization && result?.categoryUpdateRequired === true && OutlookUtils.ewsOnlySupport) {
        OutlookUtils.setEWSItemCategory(OutlookService.getItemEWSId(emailItemId), dataToSend.categoriesToAssign);
      }

      if (this.mode === ModeEnum.mailCompose
        && result.success === true
        && selectedFiles?.some(f => f.sendOnSave)
        && Array.isArray(result.succeededFiles)
        && result.succeededFiles.length > 0) {
        const sent: boolean = await OutlookUtils.sendDraft();
        if (!sent && result && typeof result === "object") {
          result.sendOnSaveFailed = true;
        }
      }

      return result;
    } catch (e) {
      logger(e, "OutlookPersistor.ts (saveToSharePoint)");
      if (e.response) {
        return e.response.data
      }
      return null;
    }
  };

  saveToOneDrive = async (
    files: IFileToUpload[],
    directory: string,
    oneDriveOverwriteOption: OneDriveOverWriteEnum,
    outlookToken: string,
    folderName: string = "",
    accountId: string,
    autoRemoveMailItemAttachments?: boolean,
    convertEmailToPDF?: boolean,
    tenantOcrLanguage?: string,
    exchangeAccountEmail?: string,
    graphMailItemCategory?: string,
    saveMode?: SaveMode
  ) => {

    const emailItemId: string = this.mode !== ModeEnum.mailCompose
      ? Office.context.mailbox?.item?.itemId
      : await OutlookUtils.getComposeModeItemId();

    if (emailItemId && this.mode === ModeEnum.mailCompose) {
      await OutlookUtils.verifyMailboxAccess(true, this.mode);
    }

    const selectedFiles: IFileToUpload[] = files.filter(file => file.isSelected);
    let dataToSend: IOneDriveDataDto = {
      graphToken: await authModule.getGraphAccessToken(),
      emailItemId: OutlookService.getItemRestId(emailItemId) ?? undefined,
      accountId: accountId,
      fileContents: selectedFiles,
      outlookRestInfo: await this.buildOutlookRestInfo(
        outlookToken,
        !!(emailItemId && this.mode === ModeEnum.mailCompose),
        selectedFiles?.find(file => file.graphMailItemId)?.graphMailItemId),
      directory: this.getSavingDirectory(directory, folderName),
      overWriteType: oneDriveOverwriteOption
    };

    if (!this.disableMailCategorization) {
      const categoryToAssign: Office.CategoryDetails = OutlookUtils.getCategoryDetails(FolderTypeEnum.oneDrive);
      if (categoryToAssign && !(await OutlookUtils.getItemCategories())?.some(x => x.displayName === categoryToAssign.displayName)) {
        dataToSend.categoriesToAssign = [categoryToAssign.displayName];
      }
    }

    dataToSend.autoRemoveMailItemAttachments = autoRemoveMailItemAttachments === true;
    dataToSend.convertEmailToPDF = convertEmailToPDF === true;
    dataToSend.tenantOcrLanguage = tenantOcrLanguage;
    dataToSend.exchangeAccountEmail = exchangeAccountEmail;
    dataToSend.graphMailItemCategory = graphMailItemCategory;
    dataToSend.saveMode = saveMode;

    try {
      const result = await apiFileSystemCallDynamicEndpoint(API.url.outlook.oneDrive, true, dataToSend);

      if (!this.disableMailCategorization && result?.categoryUpdateRequired === true && OutlookUtils.ewsOnlySupport) {
        OutlookUtils.setEWSItemCategory(OutlookService.getItemEWSId(emailItemId), dataToSend.categoriesToAssign);
      }

      if (this.mode === ModeEnum.mailCompose
        && result.success === true
        && selectedFiles?.some(f => f.sendOnSave)
        && Array.isArray(result.succeededFiles)
        && result.succeededFiles.length > 0) {
        const sent: boolean = await OutlookUtils.sendDraft();
        if (!sent && result && typeof result === "object") {
          result.sendOnSaveFailed = true;
        }
      }

      return result;
    } catch (e) {
      logger(e, "OutlookPersistor.ts (saveToOneDrive)");
      if (e.response) {
        return e.response.data
      }
      return null;
    }
  };
}
